import { includes } from 'lodash-es';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Window,
  WindowHeader,
  WindowContent,
  Frame,
  GroupBox,
  Select,
  Button,
  Hourglass,
  Table,
  TableBody,
  TableRow,
  TableDataCell,
  TableHeadCell,
  TableHead
} from 'react95';
import TaskDetailActions from './TaskDetailActions';
import { userSelector } from '../../selectors/authSelectors';
import { getStageDisplayName, getTypeDisplayName, prepareUserDropdownList } from '../../utils/taskUtils';
import './TaskDetailHeader.scss';
import { MIN_DRAKE_YEAR, RETURN_DOWNLOAD_STAGES } from '../../global/Environment';
import { useGetTaskQuery, useUpdateAssigneeMutation } from '../../api/opsSystemApi';
import { setTaskDetailResMsg } from '../../actions/taskDetailActions';
import { Link, useParams } from 'react-router-dom';
import { useQDeckUserEmailNameMap } from '../../api/qdeckApi';
import {
  useDownloadUserReturnMutation,
  useDownloadDrakeflowCsvMutation,
  useDownloadDebitCsvMutation
} from '../../api/taxesApi';
import { Stack } from '@mui/material';
import { sentryCaptureException } from '../../utils/sentryUtils';
import { TASK_TYPES } from '../../constants';
import { useTheme } from 'styled-components';

const TaskDetailHeader = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const user = useSelector(userSelector);

  const { qdeckUserEmailNameMap, isLoading: isQDeckUserEmailNameMapLoading } = useQDeckUserEmailNameMap();

  const { taskId } = useParams();
  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId);
  const [downloadUserReturn, { isLoading: isDownloadingReturn }] = useDownloadUserReturnMutation();
  const [downloadDrakeflowCsv, { isLoading: isDownloadingCsv }] = useDownloadDrakeflowCsvMutation();
  const [downloadDebitCsv, { isLoading: isDownloadingDebitCsv }] = useDownloadDebitCsvMutation();
  const [updateAssignee] = useUpdateAssigneeMutation();

  if (!task?.stage || isTaskLoading || isQDeckUserEmailNameMapLoading) {
    return (
      <Frame className='task-detail-header'>
        <Hourglass />
      </Frame>
    );
  }

  const onDownloadUserReturn = async () => {
    try {
      const response = await downloadUserReturn({ userId: task.user_id, year: task.year, taskId: task.id }).unwrap();
      const url = response?.data?.url;
      if (url) {
        window.location.assign(url);
      }
    } catch (e) {
      sentryCaptureException(e);
      dispatch(setTaskDetailResMsg({ success: false, error: 'Failed to download return' }));
    }
  };

  const onDownloadDrakeflowCsv = async () => {
    try {
      const response = await downloadDrakeflowCsv({ userId: task.user_id, year: task.year, taskId: task.id }).unwrap();
      const url = response?.data?.url;

      if (url) {
        window.open(url, '_blank');
      } else {
        dispatch(setTaskDetailResMsg({ success: false, error: 'No drakeflow url received' }));
      }
    } catch (e) {
      sentryCaptureException(e);
      dispatch(setTaskDetailResMsg({ success: false, error: 'Failed to download Drakeflow CSV' }));
    }
  };

  const onDownloadDebitCsv = async () => {
    try {
      const response = await downloadDebitCsv({ userId: task.user_id, year: task.year, taskId: task.id }).unwrap();
      const url = response?.data?.url;

      if (url) {
        window.open(url, '_blank');
      } else {
        dispatch(setTaskDetailResMsg({ success: false, error: 'No submit-debit url received' }));
      }
    } catch (e) {
      sentryCaptureException(e);
      dispatch(setTaskDetailResMsg({ success: false, error: 'Failed to download Debit CSV' }));
    }
  };

  const handleUpdateAssignee = async (assignee) => {
    const result = await updateAssignee({ taskId: task.id, assignee });
    if (result?.error) {
      dispatch(
        setTaskDetailResMsg({
          error: result.error.data.msg,
          title: 'Error assigning task'
        })
      );
    }
  };

  return (
    <div className='task-detail-header'>
      <Stack direction='row' sx={{ width: '30%' }}>
        <Frame style={{ padding: '8px', display: 'flex', gap: '8px', width: '100%' }}>
          <Frame variant='field' className='task-detail-header-stage-details'>
            <GroupBox variant='flat' label='Stage:'>
              <span>{getStageDisplayName(task.stage)}</span>
            </GroupBox>
            <div className='task-detail-header-stage-details-prev'>
              Moved from: {getStageDisplayName(task.prev_stage)}
            </div>
            {task.user_esc_historic ? (
              <div className='task-detail-header-esc'>Prev. user escs: {task.user_esc_historic}</div>
            ) : null}
            {task.rejected_esc_historic ? (
              <div className='task-detail-header-esc'>Prev. IRS rejections: {task.rejected_esc_historic}</div>
            ) : null}
            {includes(RETURN_DOWNLOAD_STAGES, task.stage) && (
              <Stack sx={{ flexDirection: 'column', gap: '8px' }}>
                {(task.type === TASK_TYPES.STANDARD || task.type === TASK_TYPES.AMENDMENT) &&
                  task.year >= MIN_DRAKE_YEAR && (
                    <Button primary onClick={onDownloadUserReturn} disabled={isDownloadingReturn}>
                      {isDownloadingReturn ? 'Download Pending...' : 'Download Return'}
                    </Button>
                  )}
                {(task.type === TASK_TYPES.AMENDMENT || task.type === TASK_TYPES.STANDARD) &&
                  task.year < MIN_DRAKE_YEAR && (
                    <Button primary onClick={onDownloadDrakeflowCsv} disabled={isDownloadingCsv}>
                      {isDownloadingCsv ? 'Generating CSV...' : 'Download Drakeflow CSV'}
                    </Button>
                  )}
              </Stack>
            )}
            {includes('Final Review', task.stage) && task.year < MIN_DRAKE_YEAR && (
              <Button
                primary
                onClick={onDownloadDebitCsv}
                disabled={isDownloadingDebitCsv}
                style={{ marginTop: '8px', width: '100%' }}>
                {isDownloadingDebitCsv ? 'Generating CSV...' : 'Download Debit CSV'}
              </Button>
            )}
          </Frame>
          <Frame variant='field' className='task-detail-header-stage-details'>
            <GroupBox
              variant='flat'
              label='Type:'
              style={{
                marginBottom: '12px',
                backgroundColor: task.type === TASK_TYPES.AMENDMENT ? theme.colors.amendmentOrange : undefined
              }}>
              <span>{getTypeDisplayName(task.type)}</span>
            </GroupBox>
            <Table data-testid='task-detail-related-tasks-table'>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Type</TableHeadCell>
                  <TableHeadCell>Action</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {task.relatedTasks.map((relatedTask) => (
                  <TableRow key={relatedTask.id}>
                    <TableDataCell>
                      <div>{getTypeDisplayName(relatedTask.type)}</div>
                    </TableDataCell>
                    <TableDataCell>
                      <Link to={`/task-detail/${relatedTask.id}`} target='_blank'>
                        <Button primary variant='flat' size='sm'>
                          Open
                        </Button>
                      </Link>
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Frame>
        </Frame>
      </Stack>

      <Frame>
        <div className='task-detail-header-assignment-container'>
          <Frame variant='field' className='task-detail-header-assignment-field'>
            <GroupBox variant='flat' label='Assigned to:'>
              <Select
                value={task.assignee || ''}
                options={prepareUserDropdownList(qdeckUserEmailNameMap)}
                width={250}
                menuMaxHeight={300}
                onChange={(opt) => handleUpdateAssignee(opt.value)}
              />
            </GroupBox>
          </Frame>

          <div className='task-detail-header-assignment-footer'>
            <Button primary variant='flat' onClick={() => handleUpdateAssignee(task.assignee ? null : user.email)}>
              {task.assignee ? 'Unassign task' : 'Assign to self'}
            </Button>
          </div>
        </div>
      </Frame>

      <Window className='task-detail-header-actions-container'>
        <WindowHeader className='task-detail-header-actions-header'>
          <div>Actions</div>
        </WindowHeader>
        <WindowContent>
          <TaskDetailActions />
        </WindowContent>
      </Window>
    </div>
  );
};

export default TaskDetailHeader;
